import type { PartnerBenefitProgram } from "./schema/ai";

export const STARTUP_BENEFITS_PATH = "/startup-benefits";
export const STARTUP_BENEFITS_PARTNER_PATH = `${STARTUP_BENEFITS_PATH}/become-a-partner`;
export const STARTUP_BENEFITS_PARTNER_CTA_LABEL = "Offer a startup benefit";

const STARTUP_BENEFIT_LOGO_HOSTS = new Set([
  "chiefintech.capital",
  "www.chiefintech.capital",
  "chiefintech.com",
  "www.chiefintech.com",
]);

export function normalizeStartupBenefitLogoUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (/^\/(?!\/)/.test(trimmed) && !trimmed.includes("\\")) return trimmed;

  try {
    const url = new URL(trimmed);
    if (url.protocol !== "https:" || !STARTUP_BENEFIT_LOGO_HOSTS.has(url.hostname.toLowerCase())) {
      return null;
    }
    return url.toString();
  } catch {
    return null;
  }
}

export function isStartupBenefitsPublicPath(pathname: string): boolean {
  const normalized = pathname.split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";
  return normalized === STARTUP_BENEFITS_PATH || normalized.startsWith(`${STARTUP_BENEFITS_PATH}/`);
}

export const STARTUP_BENEFIT_CATEGORY_PAGES = [
  {
    slug: "software-deals",
    path: `${STARTUP_BENEFITS_PATH}/software-deals`,
    label: "Software deals",
    title: "Startup Software Deals & SaaS Discounts (2026)",
    heading: "Startup Software Deals & SaaS Discounts for Founders",
    description:
      "Research startup software deals, SaaS discounts, free tools, eligibility rules, access routes, limits, and provider source links.",
    intro:
      "Find researched software deals and SaaS discounts for startup teams, including productivity, developer, design, sales, marketing, and collaboration tools.",
    matchTerms: [
      "software",
      "saas",
      "productivity",
      "developer",
      "design",
      "collaboration",
      "marketing",
      "sales",
      "crm",
      "analytics",
    ],
  },
  {
    slug: "cloud-credits",
    path: `${STARTUP_BENEFITS_PATH}/cloud-credits`,
    label: "Cloud credits",
    title: "Startup Cloud Credits for Founders (2026)",
    heading: "Startup Cloud Credits & Infrastructure Programs",
    description:
      "Research startup cloud credits, AI compute programs, hosting benefits, eligibility rules, access routes, limits, and provider source links.",
    intro:
      "Explore cloud credits, AI compute programs, hosting offers, and infrastructure benefits for eligible startups, with current access and expiry details.",
    matchTerms: [
      "cloud",
      "credit",
      "compute",
      "hosting",
      "infrastructure",
      "database",
      "ai",
      "developer",
    ],
  },
  {
    slug: "startup-discounts",
    path: `${STARTUP_BENEFITS_PATH}/startup-discounts`,
    label: "Startup discounts",
    title: "Startup Discounts & Free Tools for Founders (2026)",
    heading: "Startup Discounts & Free Tools for Founders",
    description:
      "Research startup discounts, free tools, extended trials, eligibility rules, access routes, limits, and provider source links.",
    intro:
      "Browse founder discounts, free tools, reduced-price plans, and extended trials that can lower the cost of building and operating an early-stage company.",
    matchTerms: [
      "discount",
      "free",
      "trial",
      "off",
      "reduced",
      "waived",
      "saving",
    ],
  },
  {
    slug: "founder-programs",
    path: `${STARTUP_BENEFITS_PATH}/founder-programs`,
    label: "Founder programs",
    title: "Founder Programs & Partner Benefits (2026)",
    heading: "Founder Programs & Partner Benefits",
    description:
      "Research founder programs and partner benefits, including eligibility, application routes, support, limits, and provider source links.",
    intro:
      "Discover provider startup programs, partner-only benefits, education, support, and application routes for founders and eligible portfolio companies.",
    matchTerms: [
      "founder",
      "startup program",
      "accelerator",
      "partner",
      "application",
      "support",
      "education",
      "portfolio",
    ],
  },
] as const;

export type StartupBenefitCategorySlug = typeof STARTUP_BENEFIT_CATEGORY_PAGES[number]["slug"];

export function isStartupBenefitCategorySlug(slug: string): slug is StartupBenefitCategorySlug {
  return STARTUP_BENEFIT_CATEGORY_PAGES.some((page) => page.slug === slug);
}

export function isStartupBenefitReservedSlug(slug: string): boolean {
  return isStartupBenefitCategorySlug(slug) || slug === "become-a-partner";
}

export function startupBenefitCategoryPath(slug: StartupBenefitCategorySlug | string): string {
  const page = STARTUP_BENEFIT_CATEGORY_PAGES.find((candidate) => candidate.slug === slug);
  if (!page) {
    throw new Error(`Unknown startup benefit category: ${slug}`);
  }
  return page.path;
}

export function resolveStartupBenefitCategoryPage(pathname: string) {
  const normalized = pathname.split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";
  return STARTUP_BENEFIT_CATEGORY_PAGES.find((page) => page.path === normalized) || null;
}

function normalizeStartupBenefitSearchText(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

export function matchesStartupBenefitCategoryTerms(
  values: ReadonlyArray<string | null | undefined>,
  matchTerms: readonly string[],
): boolean {
  const searchable = ` ${normalizeStartupBenefitSearchText(values.filter(Boolean).join(" "))} `;
  return matchTerms.some((term) => {
    const normalizedTerm = normalizeStartupBenefitSearchText(term);
    return normalizedTerm.length > 0 && searchable.includes(` ${normalizedTerm} `);
  });
}

export function startupBenefitDetailPath(slug: string): string {
  const normalized = slug.trim().toLowerCase();
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized)) {
    throw new Error("Startup benefit slug must be lowercase kebab-case");
  }
  if (isStartupBenefitCategorySlug(normalized)) {
    throw new Error(`Startup benefit slug is reserved for a category page: ${normalized}`);
  }
  if (isStartupBenefitReservedSlug(normalized)) {
    throw new Error(`Startup benefit slug is reserved for a static page: ${normalized}`);
  }
  return `${STARTUP_BENEFITS_PATH}/${normalized}`;
}

export function startupBenefitApplicationPath(slug: string): string {
  return `/api${startupBenefitDetailPath(slug)}/apply`;
}

export type StartupBenefitAccessType = PartnerBenefitProgram["accessType"];
export type StartupBenefitAvailabilityStatus = PartnerBenefitProgram["availabilityStatus"];
export type StartupBenefitPublicationKind = "research_resource" | "verified_offer";
export type StartupBenefitChiefInTechAvailability =
  | "research_only"
  | "verification_in_progress"
  | "active";

const accessLabels: Record<StartupBenefitAccessType, string> = {
  founder_direct: "Available directly from the provider",
  partner_application: "Provider partner application required",
  portfolio_referral: "Eligible portfolio referral required",
  member_only: "Eligible network membership required",
  platform_specific: "Available through the named third-party platform",
};

export function startupBenefitAccessLabel(accessType: StartupBenefitAccessType): string {
  return accessLabels[accessType];
}

export const STARTUP_BENEFIT_ACCESS_FILTERS: ReadonlyArray<{
  value: StartupBenefitAccessType;
  label: string;
}> = [
  { value: "founder_direct", label: "Direct from provider" },
  { value: "partner_application", label: "Partner application" },
  { value: "portfolio_referral", label: "Portfolio referral" },
  { value: "member_only", label: "Network membership" },
  { value: "platform_specific", label: "Named platform" },
];

export const STARTUP_BENEFIT_PARENT_GROUPS = [
  { slug: "founder-growth", label: "Founder growth & fundraising" },
  { slug: "ai-cloud-data", label: "AI, cloud & data" },
  { slug: "engineering-product", label: "Engineering & product" },
  { slug: "security-compliance", label: "Security & compliance" },
  { slug: "finance-legal", label: "Finance, legal & company building" },
  { slug: "sales-customers", label: "Sales, market access & customers" },
  { slug: "people-hiring", label: "People & hiring" },
  { slug: "productivity-design", label: "Productivity, design & collaboration" },
  { slug: "operations-logistics", label: "Operations, logistics & sustainability" },
  { slug: "media-accessibility", label: "Media & accessibility" },
  { slug: "other", label: "Other" },
] as const;

export type StartupBenefitParentGroup = typeof STARTUP_BENEFIT_PARENT_GROUPS[number];
export type StartupBenefitParentGroupSlug = StartupBenefitParentGroup["slug"];

const startupBenefitCategoryParentGroups: Record<string, StartupBenefitParentGroupSlug> = {
  accelerator_benchmark: "founder-growth",
  accelerator_founder_education: "founder-growth",
  accelerator_market_access: "founder-growth",
  founder_education: "founder-growth",
  founder_network: "founder-growth",
  founder_operations: "founder-growth",
  fundraising_investor_relations: "founder-growth",
  ai_hardware: "ai-cloud-data",
  ai_models: "ai-cloud-data",
  ai_product_development: "ai-cloud-data",
  blockchain_infrastructure: "ai-cloud-data",
  cloud_infrastructure: "ai-cloud-data",
  data_ai_infrastructure: "ai-cloud-data",
  data_databases: "ai-cloud-data",
  automation: "engineering-product",
  developer_education: "engineering-product",
  developer_tools: "engineering-product",
  product_analytics: "engineering-product",
  product_development: "engineering-product",
  product_management: "engineering-product",
  product_operations: "engineering-product",
  search_discovery: "engineering-product",
  web_development: "engineering-product",
  security_compliance: "security-compliance",
  security_identity: "security-compliance",
  crypto_payments: "finance-legal",
  equity_management: "finance-legal",
  finance_accounting: "finance-legal",
  finance_banking: "finance-legal",
  finance_company_building: "finance-legal",
  finance_company_operations: "finance-legal",
  finance_customer_operations: "finance-legal",
  legal_company_formation: "finance-legal",
  ai_customer_support: "sales-customers",
  gtm_customer_operations: "sales-customers",
  market_entry: "sales-customers",
  sales_staffing: "sales-customers",
  hiring_recruiting: "people-hiring",
  people_operations: "people-hiring",
  communications: "productivity-design",
  content_collaboration: "productivity-design",
  data_productivity: "productivity-design",
  design_creative: "productivity-design",
  document_workflow: "productivity-design",
  productivity_collaboration: "productivity-design",
  hardware_supply_chain: "operations-logistics",
  logistics_gifting: "operations-logistics",
  sustainability: "operations-logistics",
  travel_operations: "operations-logistics",
  media_accessibility: "media-accessibility",
  media_infrastructure: "media-accessibility",
};

export function startupBenefitParentGroupForCategory(category: string): StartupBenefitParentGroup {
  const slug = startupBenefitCategoryParentGroups[category.trim().toLowerCase()] || "other";
  return STARTUP_BENEFIT_PARENT_GROUPS.find((group) => group.slug === slug)!;
}

const categoryLabelOverrides: Record<string, string> = {
  data_ai_infrastructure: "Data & AI infrastructure",
  fundraising_investor_relations: "Fundraising & investor relations",
  hr_payroll_benefits: "HR, payroll & benefits",
  productivity_collaboration: "Productivity & collaboration",
  sales_crm: "Sales & CRM",
};

const categoryAcronyms = new Map([
  ["ai", "AI"],
  ["api", "API"],
  ["crm", "CRM"],
  ["devops", "DevOps"],
  ["gdpr", "GDPR"],
  ["gtm", "GTM"],
  ["hr", "HR"],
  ["ip", "IP"],
  ["qa", "QA"],
  ["seo", "SEO"],
  ["soc", "SOC"],
  ["ui", "UI"],
  ["ux", "UX"],
  ["vc", "VC"],
]);

export function startupBenefitCategoryLabel(category: string): string {
  const key = category.trim().toLowerCase();
  if (!key) return "Other";
  const override = categoryLabelOverrides[key];
  if (override) return override;

  const words = key
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word) => categoryAcronyms.get(word) || word);
  const label = words.join(" ");
  return label.charAt(0).toUpperCase() + label.slice(1);
}

const availabilityLabels: Record<StartupBenefitAvailabilityStatus, string> = {
  draft: "Draft",
  review_required: "Research resource",
  verified: "Offer verified",
  paused: "Offer paused",
  expired: "Offer expired",
};

export function startupBenefitAvailabilityLabel(status: StartupBenefitAvailabilityStatus): string {
  return availabilityLabels[status];
}

const currentlyAvailableStartupBenefitSourceStatuses = new Set([
  "ACTIVE_PUBLIC",
  "BENCHMARK_ONLY",
  "CONDITIONAL_BUNDLE",
  "NEGOTIATED",
]);

const knownUnavailableStartupBenefitSourceStatuses = new Set([
  "CLOSED",
  "DISCONTINUED",
  "ENDED",
  "EXPIRED",
  "INACTIVE",
  "RETIRED",
  "SUSPENDED",
  "CANCELLED",
]);

export function isStartupBenefitSourceCurrentlyAvailable(status: string | null | undefined): boolean {
  return Boolean(
    status
    && currentlyAvailableStartupBenefitSourceStatuses.has(status.trim().toUpperCase()),
  );
}

export function isStartupBenefitSourceKnownUnavailable(status: string | null | undefined): boolean {
  return Boolean(
    status
    && knownUnavailableStartupBenefitSourceStatuses.has(status.trim().toUpperCase()),
  );
}

export function startupBenefitSourceStatusLabel(status: string | null | undefined): string {
  if (!status) return "Availability not classified";
  const normalized = status.trim().toUpperCase();
  const knownLabels: Record<string, string> = {
    ACTIVE_PUBLIC: "Active public program",
    BENCHMARK_ONLY: "Third-party benchmark route",
    CONDITIONAL_BUNDLE: "Conditional bundle",
    NEGOTIATED: "Negotiated access route",
    VERIFY_BEFORE_PROMOTION: "Verify before promotion",
    WATCHLIST: "Watchlist",
    CLOSED: "Program closed",
    DISCONTINUED: "Program discontinued",
    ENDED: "Program ended",
    EXPIRED: "Program expired",
    INACTIVE: "Program inactive",
    RETIRED: "Program retired",
    SUSPENDED: "Program suspended",
    CANCELLED: "Program cancelled",
  };
  return knownLabels[normalized] || startupBenefitCategoryLabel(normalized);
}

export interface StartupBenefitPublicPartner {
  name: string;
  slug: string | null;
  logoUrl: string | null;
  website: string | null;
  websiteVerified: boolean;
  ecosystemProfileSlug: string | null;
}

export interface StartupBenefitPublicRecord {
  id: number;
  slug: string;
  name: string;
  category: string;
  accessType: StartupBenefitAccessType;
  availabilityStatus: StartupBenefitAvailabilityStatus;
  publicationKind: StartupBenefitPublicationKind;
  accessLabel: string;
  headline: string | null;
  summary: string | null;
  fullDescription: string | null;
  eligibilitySummary: string | null;
  geography: string | null;
  howToAccess: string | null;
  limitsAndExpiry: string | null;
  sourceUrl: string;
  applicationUrl: string | null;
  termsUrl: string | null;
  disclosureText: string | null;
  sourceCheckedAt: string;
  sourceStatus: string | null;
  isCurrentlyAvailable: boolean;
  provider: StartupBenefitPublicPartner;
  offeringPlatform: StartupBenefitPublicPartner | null;
  chiefInTechAvailability: StartupBenefitChiefInTechAvailability;
  isAvailableThroughChiefInTech: boolean;
}

export type StartupBenefitAccessPresentationResource = Pick<
  StartupBenefitPublicRecord,
  | "publicationKind"
  | "availabilityStatus"
  | "sourceStatus"
  | "isCurrentlyAvailable"
  | "chiefInTechAvailability"
  | "isAvailableThroughChiefInTech"
>;

export function isStartupBenefitChiefInTechAccessConsistent({
  publicationKind,
  availabilityStatus,
  chiefInTechAvailability,
  isAvailableThroughChiefInTech,
}: Pick<
  StartupBenefitAccessPresentationResource,
  | "publicationKind"
  | "availabilityStatus"
  | "chiefInTechAvailability"
  | "isAvailableThroughChiefInTech"
>): boolean {
  if (chiefInTechAvailability === "active") {
    return isAvailableThroughChiefInTech
      && publicationKind === "verified_offer"
      && availabilityStatus === "verified";
  }
  if (chiefInTechAvailability === "research_only"
      || chiefInTechAvailability === "verification_in_progress") {
    return !isAvailableThroughChiefInTech;
  }
  return false;
}

export function isStartupBenefitVerifiedOfferPresentation({
  publicationKind,
  availabilityStatus,
  sourceStatus,
  isCurrentlyAvailable,
  chiefInTechAvailability,
  isAvailableThroughChiefInTech,
}: StartupBenefitAccessPresentationResource): boolean {
  return publicationKind === "verified_offer"
    && availabilityStatus === "verified"
    && isCurrentlyAvailable
    && isStartupBenefitSourceCurrentlyAvailable(sourceStatus)
    && isStartupBenefitChiefInTechAccessConsistent({
      publicationKind,
      availabilityStatus,
      chiefInTechAvailability,
      isAvailableThroughChiefInTech,
    });
}

export function isStartupBenefitActionablePresentation(
  benefit: StartupBenefitAccessPresentationResource,
): boolean {
  if (!benefit.isCurrentlyAvailable
      || !isStartupBenefitSourceCurrentlyAvailable(benefit.sourceStatus)
      || !isStartupBenefitChiefInTechAccessConsistent(benefit)) {
    return false;
  }
  if (benefit.publicationKind === "verified_offer") {
    return isStartupBenefitVerifiedOfferPresentation(benefit);
  }
  return benefit.publicationKind === "research_resource"
    && benefit.availabilityStatus === "review_required";
}

export function isStartupBenefitVerificationInProgressPresentation(
  benefit: StartupBenefitAccessPresentationResource,
): boolean {
  return benefit.chiefInTechAvailability === "verification_in_progress"
    && isStartupBenefitActionablePresentation(benefit);
}

export interface StartupBenefitsPublicResponse {
  benefits: StartupBenefitPublicRecord[];
  categories: string[];
}

const founderAccessPriority: Record<StartupBenefitAccessType, number> = {
  founder_direct: 0,
  partner_application: 1,
  portfolio_referral: 2,
  member_only: 3,
  platform_specific: 4,
};

type FounderSortableBenefit = Pick<
  StartupBenefitPublicRecord,
  | "slug"
  | "name"
  | "accessType"
  | "publicationKind"
  | "availabilityStatus"
  | "sourceStatus"
  | "isCurrentlyAvailable"
  | "chiefInTechAvailability"
  | "isAvailableThroughChiefInTech"
> & Partial<Pick<
  StartupBenefitPublicRecord,
  "sourceCheckedAt" | "headline" | "summary" | "fullDescription" | "eligibilitySummary" | "geography" | "limitsAndExpiry"
>>;

function startupBenefitFreshnessBucket(value: string | undefined, now = Date.now()): number {
  if (!value) return 0;
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return 0;
  const ageDays = Math.max(0, (now - timestamp) / 86_400_000);
  if (ageDays <= 90) return 4;
  if (ageDays <= 180) return 3;
  if (ageDays <= 365) return 2;
  if (ageDays <= 730) return 1;
  return 0;
}

function hasBroadGeographySignal(value: string | null | undefined): boolean {
  return /\b(global|worldwide|international|most countries)\b/i.test(value || "");
}

function startupBenefitValueScore(benefit: FounderSortableBenefit): number {
  const text = [benefit.headline, benefit.summary, benefit.fullDescription]
    .filter(Boolean)
    .join(". ");
  const localPrefixBefore = (start: number, length = 96) => {
    const prefix = text.slice(Math.max(0, start - length), start);
    const clauseBoundary = Math.max(
      prefix.lastIndexOf(","),
      prefix.lastIndexOf(";"),
      prefix.lastIndexOf("."),
      prefix.lastIndexOf("!"),
      prefix.lastIndexOf("?"),
      prefix.lastIndexOf(":"),
    );
    return prefix.slice(clauseBoundary + 1);
  };
  const currencyMatches = Array.from(text.matchAll(/[$€£]\s?([\d,.]+)\s?([km])?/gi))
    .filter((match) => {
      const start = match.index || 0;
      const end = start + match[0].length;
      const before = localPrefixBefore(start);
      const after = text.slice(end, Math.min(text.length, end + 48));
      const describesBusinessMetric = /^\s*(?:(?:in|of)\s+)?(?:annual(?:\s+recurring)?\s+)?(?:revenue|arr|valuation|funding|raised|deposited?|processed|gmv|spend|sales)\b/i.test(after);
      if (describesBusinessMetric) return false;
      const describesValue = /^\s*(?:(?:in|of|toward)\s+)?(?:[a-z0-9+.-]+\s+){0,3}(?:credit|credits|cash|grant|grants|rebate|cashback|saving|savings|discount|off)\b/i.test(after);
      const introducedAsValue = /\b(?:benefit|offer|package|bundle|credits?|grants?|rebate|cashback|savings?|discount)\s+(?:of\s+)?(?:up to|worth|valued at|value of|amount of)\s*$/i.test(before);
      return describesValue || introducedAsValue;
    });
  const currencyAmounts = currencyMatches.map((match) => {
    const base = Number(match[1].replace(/,/g, ""));
    const multiplier = match[2]?.toLowerCase() === "m" ? 1_000_000 : match[2]?.toLowerCase() === "k" ? 1_000 : 1;
    return Number.isFinite(base) ? base * multiplier : 0;
  });
  const percentages = Array.from(text.matchAll(/\b(\d+(?:\.\d+)?)\s?%/g))
    .filter((match) => {
      const start = match.index || 0;
      const end = start + match[0].length;
      const before = localPrefixBefore(start);
      const after = text.slice(end, Math.min(text.length, end + 48));
      const introducedAsValue = /\b(?:discount|cashback|rebate|savings?|save)\s+(?:of|up to)?\s*$/i.test(before);
      const namedValue = /^\s*(?:discount|cashback|rebate|saving|savings)\b/i.test(after);
      const offValue = /^\s*off(?=\s*(?:$|[.,;!?])|\s+(?:for|on|your|all|any|first|annual|monthly|startup|standard|paid|subscriptions?|plans?|tiers?|fees?|costs?|products?|services?|software|tools?|months?|years?|billing)\b|\s+the\s+(?:first|annual|monthly|subscription|plan|tier|fee|cost|product|service|software|tool)\b)/i.test(after);
      return introducedAsValue || namedValue || offValue;
    })
    .map((match) => Number(match[1]));
  const currencyScore = Math.min(240, Math.round(Math.log10(Math.max(0, ...currencyAmounts) + 1) * 40));
  const percentageScore = Math.min(200, Math.round(Math.max(0, ...percentages) * 2));
  const freeScore = /\b(?:free\s+(?:(?:startup|starter)\s+)?(?:plan|tier|credit|credits|month|months|year|access|subscription|trial|tool|tools|software)|for free)\b/i.test(text) ? 120 : 0;
  const waivedScore = /\b(?:waived?\s+(?:fee|fees|cost|costs)|(?:fee|fees|cost|costs)\s+(?:(?:are|fully)\s+)?waived?)\b/i.test(text) ? 90 : 0;
  return Math.max(currencyScore, percentageScore, freeScore, waivedScore);
}

function hasBroadEligibilitySignal(value: string | null | undefined): boolean {
  return /\b(all startups|any startup|open to startups|self-service|no (?:partner|membership|referral) required)\b/i.test(value || "");
}

function startupBenefitExpiryPenalty(value: string | null | undefined): number {
  const text = value || "";
  if (/\b(expired|closed|ended|no longer available|discontinued)\b/i.test(text)) return 500;
  if (/\b(limited time|expires soon|while supplies last)\b/i.test(text)) return 40;
  return 0;
}

export function startupBenefitFounderUtilityScore(benefit: FounderSortableBenefit): number {
  const currentlyAvailable = benefit.isCurrentlyAvailable
    && isStartupBenefitSourceCurrentlyAvailable(benefit.sourceStatus);
  const verifiedOffer = isStartupBenefitVerifiedOfferPresentation(benefit);
  const availableThroughChiefInTech = verifiedOffer
    && benefit.isAvailableThroughChiefInTech;
  return Number(availableThroughChiefInTech) * 1_000
    + Number(verifiedOffer) * 500
    + startupBenefitValueScore(benefit)
    + (4 - founderAccessPriority[benefit.accessType]) * 40
    + Number(hasBroadEligibilitySignal(benefit.eligibilitySummary)) * 80
    + Number(hasBroadGeographySignal(benefit.geography)) * 40
    + startupBenefitFreshnessBucket(benefit.sourceCheckedAt) * 20
    - Number(!currentlyAvailable) * 2_000
    - startupBenefitExpiryPenalty(benefit.limitsAndExpiry);
}

export function sortStartupBenefitsForFounders<T extends FounderSortableBenefit>(
  benefits: ReadonlyArray<T>,
): T[] {
  return [...benefits].sort((left, right) =>
    startupBenefitFounderUtilityScore(right) - startupBenefitFounderUtilityScore(left)
    || left.name.localeCompare(right.name)
    || left.slug.localeCompare(right.slug),
  );
}

export function startupBenefitPublicDescription(benefit: StartupBenefitPublicRecord): string {
  return benefit.summary || benefit.fullDescription ||
    `Research ${benefit.name} from ${benefit.provider.name}, including eligibility, access instructions, limits, and the provider source.`;
}

export interface StartupBenefitRelatedSummary {
  id: number;
  slug: string;
  name: string;
  headline: string | null;
  category: string;
  accessType: StartupBenefitAccessType;
  accessLabel: string;
  provider: Pick<StartupBenefitPublicPartner, "name" | "logoUrl">;
}

export function selectRelatedStartupBenefits(
  current: Pick<StartupBenefitPublicRecord, "id" | "slug" | "category" | "accessType">,
  candidates: ReadonlyArray<
    Pick<
      StartupBenefitPublicRecord,
      "id" | "slug" | "name" | "headline" | "category" | "accessType" | "provider"
    >
  >,
  limit = 3,
): StartupBenefitRelatedSummary[] {
  const safeLimit = Number.isFinite(limit) ? Math.max(0, Math.floor(limit)) : 3;

  return candidates
    .filter((candidate) => candidate.id !== current.id && candidate.slug !== current.slug)
    .map((candidate) => ({
      candidate,
      score:
        (candidate.category === current.category ? 4 : 0)
        + (candidate.accessType === current.accessType ? 2 : 0),
    }))
    .sort((left, right) =>
      right.score - left.score
      || (left.candidate.headline || left.candidate.name).localeCompare(
        right.candidate.headline || right.candidate.name,
      )
      || left.candidate.slug.localeCompare(right.candidate.slug),
    )
    .slice(0, safeLimit)
    .map(({ candidate }) => ({
      id: candidate.id,
      slug: candidate.slug,
      name: candidate.name,
      headline: candidate.headline,
      category: candidate.category,
      accessType: candidate.accessType,
      accessLabel: startupBenefitAccessLabel(candidate.accessType),
      provider: {
        name: candidate.provider.name,
        logoUrl: candidate.provider.logoUrl,
      },
    }));
}
